import React, { useState, useEffect, useMemo } from "react";
import { EntityTablePage, DeleteConfirmationConfig } from "@/components/EntityTablePage";
import { getIncidentTableConfig } from "@/config/tableConfigs/incidentTableConfig";
import { incidentService } from "@/services/incidentService";
import { IncidentType } from "@/types/db/incidents";
import useAuth from "@/hooks/useAuth";
import { FromDB } from "@/types";
import { supabase } from "@/lib/supabase-client";
import { NoPermissionScreen } from "@/components/screens/shared/NoPermissionScreen";
import { DeleteIncidentModal } from "@/components/Modals/DeleteIncidentModal";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useSetPageTitle } from "@/hooks/useSetPageTitle";

export default function IncidentsPage() {
  const { userRoles, displayNames } = useAuth();
  const [incidentTypes, setIncidentTypes] = useState<{ value: string; color?: string }[]>([]);

  useSetPageTitle(displayNames?.incidents?.plural);

  // Check if user has incidents read permission
  const hasIncidentsAccess = useMemo(() => {
    return userRoles?.some((r) => r.permissions?.incidents?.read === true) ?? false;
  }, [userRoles]);

  const tableConfig = getIncidentTableConfig(userRoles, displayNames);

  // Merge dynamic incident type options into filterChoices
  const filterChoicesWithOptions = useMemo(() => {
    return tableConfig.filterChoices.map((choice) => {
      if (choice.name === "incident_type") {
        return { ...choice, options: incidentTypes };
      }
      return choice;
    });
  }, [tableConfig.filterChoices, incidentTypes]);

  // Fetch incident types for filter options
  useEffect(() => {
    if (!hasIncidentsAccess) return;

    const fetchIncidentTypes = async () => {
      const { data } = await supabase
        .schema("lists")
        .from("incident_types")
        .select("name")
        .order("name", { ascending: true });

      if (data) {
        setIncidentTypes(data.map((t) => ({ value: t.name })));
      }
    };
    fetchIncidentTypes();
  }, [hasIncidentsAccess]);

  const deleteConfirmation: DeleteConfirmationConfig<FromDB<IncidentType>> = useMemo(
    () => ({
      storageKey: "incident_delete_skip_confirmation",
      renderModal: ({ count, onConfirm, onCancel }) => (
        <DeleteIncidentModal
          count={count}
          onConfirm={async (dontShowAgain) => {
            if (dontShowAgain) {
              await AsyncStorage.setItem("incident_delete_skip_confirmation", "true");
            }
            onConfirm();
          }}
          onCancel={onCancel}
        />
      ),
    }),
    [],
  );

  if (!hasIncidentsAccess) {
    return <NoPermissionScreen entityName={displayNames?.incidents?.plural || "Incidents"} />;
  }

  return (
    <EntityTablePage<FromDB<IncidentType>>
      {...tableConfig}
      filterChoices={filterChoicesWithOptions}
      fetchData={incidentService.getIncidents}
      updateEntities={incidentService.saveRowPatches}
      deleteEntities={incidentService.deleteIncidentsByIds}
      deleteConfirmation={deleteConfirmation}
    />
  );
}
